import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { addWorkout, getFitnessSummary, updateWorkout, deleteWorkout, estimateWorkoutCalories } from '../lib/api';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { useSmartComment } from '../hooks/useSmartComment';
import { VoiceInput } from '../components/VoiceInput';
import { MemorySuggestions } from '../components/MemorySuggestions';
import { CalendarStrip } from '../components/CalendarStrip';
import { WeatherTipsCard } from '../components/WeatherTipsCard';
import { TrainingWindowsCard } from '../components/TrainingWindowsCard';
import { PeriodPicker } from '../components/PeriodPicker';
import { WorkoutPicker } from '../components/WorkoutPicker';
import { DurationPicker } from '../components/DurationPicker';
import { WORKOUT_TYPES, getWorkoutType } from '../lib/workoutTypes';
import { WorkoutHistoryList } from '../components/fitness/WorkoutHistoryList';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { Dumbbell, Plus, Flame, Clock, Activity, Pencil, Trash2, Check, X, ChevronDown, Loader2, Footprints, Star } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { de, enUS } from 'date-fns/locale';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Fitness() {
    const { language } = useLanguage();
    const [summary, setSummary] = useState(null);
    const [summaryDays, setSummaryDays] = useState(7);
    const [loading, setLoading] = useState(true);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [quickLogOpen, setQuickLogOpen] = useState(false);
    const [editingEntry, setEditingEntry] = useState(null);
    const [showHistory, setShowHistory] = useState(false);
    const [workoutList, setWorkoutList] = useState([]);
    const [favoriteWorkouts, setFavoriteWorkouts] = useState([]);
    const [stepsHistory, setStepsHistory] = useState([]);
    const [stepsInput, setStepsInput] = useState('');
    const [todaySteps, setTodaySteps] = useState(0);
    const [stepsLoading, setStepsLoading] = useState(false);
    const { fetchComment } = useSmartComment();
    
    // Quick log form
    const [quickForm, setQuickForm] = useState({
        workout_type: 'strength',
        duration_seconds: 30 * 60,
        calories_burned: 0,
        notes: '',
        location: '',
        golf_holes: 18
    });
    const [quickLoading, setQuickLoading] = useState(false);
    const [estimating, setEstimating] = useState(false);
    const [savingFavorite, setSavingFavorite] = useState(false);

    useEffect(() => {
        loadSummary();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- re-run when period changes
    }, [summaryDays]);

    useEffect(() => {
        loadWorkoutList();
        loadStepsHistory();
        loadTodaySteps();
        loadFavoriteWorkouts();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only loaders; functions are stable closures
    }, []);

    const loadFavoriteWorkouts = async () => {
        try {
            const res = await axios.get(`${API}/api/favorites/workouts`);
            setFavoriteWorkouts(Array.isArray(res.data) ? res.data : []);
        } catch (e) { console.error('Favorites load error:', e); }
    };

    const loadSummary = async () => {
        try {
            const response = await getFitnessSummary(summaryDays);
            setSummary(response.data);
        } catch (error) {
            console.error('Failed to load fitness data:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadWorkoutList = async () => {
        try {
            const res = await axios.get(`${API}/api/fitness/workouts?days=30`);
            setWorkoutList(Array.isArray(res.data) ? res.data : []);
        } catch (e) { console.error('Workout list error:', e); }
    };

    const loadStepsHistory = async () => {
        try {
            const res = await axios.get(`${API}/api/fitness/steps?days=7`);
            setStepsHistory(res.data?.entries || []);
        } catch (e) { console.error('Steps history error:', e); }
    };

    const loadTodaySteps = async () => {
        try {
            const res = await axios.get(`${API}/api/fitness/steps/today`);
            setTodaySteps(res.data.steps || 0);
        } catch (e) { console.error('Today steps error:', e); }
    };

    const handleSaveSteps = async () => {
        const steps = parseInt(stepsInput);
        if (!steps || steps < 0) return;
        setStepsLoading(true);
        try {
            const dateStr = selectedDate.toISOString().split('T')[0];
            const res = await axios.post(`${API}/api/fitness/steps`, 
                { steps, date: dateStr });
            setTodaySteps(steps);
            setStepsInput('');
            const xp = res.data.xp_earned || 0;
            toast.success(`${steps.toLocaleString()} ${language === 'de' ? 'Schritte gespeichert' : 'steps saved'}${xp > 0 ? ` +${xp} XP` : ''}`);
            loadStepsHistory();
        } catch (e) { toast.error(language === 'de' ? 'Fehler' : 'Error'); }
        finally { setStepsLoading(false); }
    };

    // Auto-estimate calories
    const autoEstimateCalories = async (workoutType, durationSeconds) => {
        const minutes = Math.round((durationSeconds || 0) / 60);
        if (!minutes || minutes < 1) {
            setQuickForm(prev => ({ ...prev, calories_burned: 0 }));
            return;
        }
        setEstimating(true);
        try {
            const response = await estimateWorkoutCalories(workoutType, minutes);
            setQuickForm(prev => ({ ...prev, calories_burned: response.data.estimated_calories }));
        } catch (error) {
            console.error('Failed to estimate:', error);
        } finally {
            setEstimating(false);
        }
    };

    useEffect(() => {
        if (quickLogOpen && quickForm.duration_seconds > 0) {
            const timer = setTimeout(() => {
                autoEstimateCalories(quickForm.workout_type, quickForm.duration_seconds);
            }, 500);
            return () => clearTimeout(timer);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- autoEstimateCalories is a stable component-scope helper
    }, [quickForm.workout_type, quickForm.duration_seconds, quickLogOpen]);

    const handleQuickLog = async () => {
        setQuickLoading(true);
        try {
            const totalSeconds = quickForm.duration_seconds || 0;
            const minutes = Math.max(1, Math.round(totalSeconds / 60));
            await addWorkout({
                workout_type: quickForm.workout_type,
                duration_minutes: minutes,
                duration_seconds: totalSeconds,
                calories_burned: quickForm.calories_burned,
                golf_holes: quickForm.golf_holes,
                name: (() => {
                    const base = getWorkoutType(quickForm.workout_type).label[language];
                    const golf = quickForm.workout_type === 'golf' ? ` (${quickForm.golf_holes} Loch)` : '';
                    return base + golf;
                })(),
                notes: [quickForm.notes, quickForm.location ? `Ort: ${quickForm.location}` : ''].filter(Boolean).join(' | '),
                logged_at: selectedDate.toISOString()
            });
            toast.success(language === 'de' ? '💪 Workout gespeichert! +15 XP' : '💪 Workout saved! +15 XP');
            setQuickLogOpen(false);
            setQuickForm({ workout_type: 'strength', duration_seconds: 30 * 60, calories_burned: 0, notes: '', location: '', golf_holes: 18 });
            loadSummary(); loadWorkoutList();
            
            // Smart comment from Nanouk
            const workoutName = getWorkoutType(quickForm.workout_type).label[language];
            fetchComment(quickForm.workout_type, `${workoutName}${quickForm.location ? ' — ' + quickForm.location : ''}`, language);
        } catch (error) {
            toast.error(language === 'de' ? 'Fehler' : 'Error');
        } finally {
            setQuickLoading(false);
        }
    };

    const handleSaveAsFavorite = async () => {
        if (savingFavorite) return;
        setSavingFavorite(true);
        try {
            const minutes = Math.max(1, Math.round((quickForm.duration_seconds || 0) / 60));
            const name = getWorkoutType(quickForm.workout_type).label[language];
            await axios.post(`${API}/api/favorites/workouts`,
                {
                    name,
                    workout_type: quickForm.workout_type,
                    duration_minutes: minutes,
                    calories_burned: quickForm.calories_burned || 0,
                });
            toast.success(language === 'de' ? '⭐ Als Favorit gespeichert' : '⭐ Saved as favorite');
            await loadFavoriteWorkouts();
        } catch (e) {
            console.error('Save favorite error:', e);
            toast.error(language === 'de' ? 'Favorit konnte nicht gespeichert werden' : 'Could not save favorite');
        } finally {
            setSavingFavorite(false);
        }
    };

    const handleUpdateWorkout = async (id, data) => {
        try {
            await updateWorkout(id, data);
            toast.success(language === 'de' ? 'Aktualisiert' : 'Updated');
            setEditingEntry(null);
            loadSummary(); loadWorkoutList();
        } catch (error) {
            toast.error(language === 'de' ? 'Fehler' : 'Error');
        }
    };

    const handleDeleteWorkout = async (id) => {
        try {
            await deleteWorkout(id);
            toast.success(language === 'de' ? 'Gelöscht' : 'Deleted');
            loadSummary(); loadWorkoutList();
        } catch (error) {
            toast.error(language === 'de' ? 'Fehler' : 'Error');
        }
    };

    const pieData = Object.entries(summary?.workouts_by_type || {}).map(([type, count]) => ({
        name: getWorkoutType(type).label[language],
        value: count,
        color: getWorkoutType(type).color
    }));

    const totalWorkouts = summary?.total_workouts || 0;
    const totalCalories = summary?.total_calories_burned || 0;
    const totalMinutes = summary?.total_duration_minutes || 0;
    const stepCalories = summary?.step_calories_burned || 0;
    const stepMinutes = summary?.step_duration_minutes || 0;
    const totalSteps = summary?.total_steps || 0;

    return (
        <div className="pb-24 lg:pb-8" data-testid="fitness-page">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="max-w-4xl mx-auto space-y-4"
            >
                {/* Sticky Header */}
                <div className="sticky top-0 z-20 bg-background/95 backdrop-blur-lg -mx-4 px-4 pt-4 pb-3 border-b border-border">
                    <div className="flex items-center justify-between mb-3">
                        <h1 className="font-display text-xl lg:text-2xl font-bold flex items-center gap-2">
                            <Dumbbell className="w-6 h-6 icon-fitness" />
                            Fitness
                        </h1>
                        <Button 
                            className="rounded-full h-10 px-4 bg-primary text-primary-foreground hover:bg-primary/90"
                            onClick={() => setQuickLogOpen(true)}
                            data-testid="add-workout-btn"
                        >
                            <Plus className="w-5 h-5 mr-1" />
                            {language === 'de' ? 'Workout' : 'Workout'}
                        </Button>
                    </div>
                    
                    <CalendarStrip 
                        selectedDate={selectedDate}
                        onDateChange={setSelectedDate}
                    />
                </div>

                <div className="px-4 space-y-4">
                    {/* Weather-aware Nanouk tips */}
                    <WeatherTipsCard category="fitness" />

                    {/* Hourly training windows */}
                    <TrainingWindowsCard />

                    {/* Period selector + Stats Row */}
                    <div className="space-y-2">
                        <PeriodPicker
                            value={summaryDays}
                            onChange={setSummaryDays}
                            testid="fitness-period"
                        />

                        <div className="grid grid-cols-3 gap-2">
                            <Card className="module-fitness border">
                                <CardContent className="p-3 text-center">
                                    <Dumbbell className="w-5 h-5 mx-auto icon-fitness mb-1" />
                                    <p className="text-xl font-bold">{totalWorkouts}</p>
                                    <p className="text-xs text-muted-foreground">{language === 'de' ? 'Workouts' : 'Workouts'}</p>
                                </CardContent>
                            </Card>
                            <Card className="module-health border">
                                <CardContent className="p-3 text-center">
                                    <Flame className="w-5 h-5 mx-auto icon-health mb-1" />
                                    <p className="text-xl font-bold">{totalCalories}</p>
                                    <p className="text-xs text-muted-foreground">kcal</p>
                                    {stepCalories > 0 && (
                                        <p className="text-[10px] text-emerald-600 dark:text-emerald-400 mt-0.5" data-testid="fitness-step-kcal-breakdown">
                                            +{stepCalories} {language === 'de' ? 'durch Schritte' : 'from steps'}
                                        </p>
                                    )}
                                </CardContent>
                            </Card>
                            <Card className="module-travel border">
                                <CardContent className="p-3 text-center">
                                    <Clock className="w-5 h-5 mx-auto icon-travel mb-1" />
                                    <p className="text-xl font-bold">{totalMinutes}</p>
                                    <p className="text-xs text-muted-foreground">min</p>
                                    {stepMinutes > 0 && (
                                        <p className="text-[10px] text-emerald-600 dark:text-emerald-400 mt-0.5" data-testid="fitness-step-min-breakdown">
                                            +{stepMinutes} {language === 'de' ? 'gehend' : 'walking'}
                                        </p>
                                    )}
                                </CardContent>
                            </Card>
                        </div>
                        {totalSteps > 0 && (
                            <p className="text-[11px] text-muted-foreground text-center" data-testid="fitness-total-steps-note">
                                {language === 'de'
                                    ? `${totalSteps.toLocaleString('de-DE')} Schritte in diesem Zeitraum`
                                    : `${totalSteps.toLocaleString('en-US')} steps in this period`}
                            </p>
                        )}
                    </div>

                    {/* Steps Input */}
                    <Card className="border" data-testid="steps-card">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3 mb-3">
                                <Footprints className="w-5 h-5 text-primary" />
                                <div className="flex-1">
                                    <p className="font-semibold text-sm">{language === 'de' ? 'Schritte von iPhone / Uhr' : 'Steps from iPhone / Watch'}</p>
                                    {todaySteps > 0 && (
                                        <p className="text-xs text-muted-foreground">
                                            {language === 'de' ? 'Heute' : 'Today'}: <span className="font-bold text-primary">{todaySteps.toLocaleString()}</span>
                                        </p>
                                    )}
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <Input
                                    type="number"
                                    value={stepsInput}
                                    onChange={(e) => setStepsInput(e.target.value)}
                                    placeholder={language === 'de' ? 'z.B. 8500' : 'e.g. 8500'}
                                    className="bg-muted border-2 border-border h-11 flex-1"
                                    data-testid="steps-input"
                                />
                                <Button
                                    onClick={handleSaveSteps}
                                    disabled={stepsLoading || !stepsInput}
                                    className="h-11 px-5 bg-primary text-primary-foreground hover:bg-primary/90"
                                    data-testid="steps-save-btn"
                                >
                                    {stepsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                                </Button>
                            </div>
                            <div className="flex gap-2 mt-2">
                                {[5000, 8000, 10000, 12000].map((s) => (
                                    <button key={s} onClick={() => setStepsInput(String(s))} className="flex-1 py-1.5 text-xs font-medium rounded-lg bg-muted hover:bg-muted/80 text-muted-foreground transition-colors">
                                        {(s/1000).toFixed(0)}k
                                    </button>
                                ))}
                            </div>
                            {stepsHistory.length > 0 && (
                                <div className="mt-3 pt-3 border-t border-border">
                                    <p className="text-xs text-muted-foreground mb-2">{language === 'de' ? 'Letzte 7 Tage' : 'Last 7 days'}</p>
                                    <div className="flex gap-1">
                                        {stepsHistory.slice(0, 7).reverse().map((s) => {
                                            const steps = s.steps || 0;
                                            const pct = Math.min(steps / 10000 * 100, 100);
                                            return (
                                                <div key={s.date} className="flex-1 text-center">
                                                    <div className="h-12 bg-muted rounded-md relative overflow-hidden">
                                                        <div className="absolute bottom-0 left-0 right-0 bg-primary/30 rounded-md" style={{ height: `${pct}%` }} />
                                                    </div>
                                                    <p className="text-[8px] text-muted-foreground mt-0.5">{s.date?.slice(8)}</p>
                                                    <p className="text-[9px] font-medium">{(steps/1000).toFixed(1)}k</p>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Pie Chart */}
                    {pieData.length > 0 && (
                        <Card className="bg-card border-border">
                            <CardContent className="p-4">
                                <h3 className="text-sm font-medium text-muted-foreground mb-2">
                                    {language === 'de' ? 'Workout-Verteilung' : 'Workout Distribution'}
                                </h3>
                                <div className="flex items-center">
                                    <div className="w-24 h-24">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie
                                                    data={pieData}
                                                    cx="50%"
                                                    cy="50%"
                                                    innerRadius={25}
                                                    outerRadius={40}
                                                    dataKey="value"
                                                >
                                                    {pieData.map((entry) => (
                                                        <Cell key={entry.name} fill={entry.color} />
                                                    ))}
                                                </Pie>
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </div>
                                    <div className="flex-1 grid grid-cols-2 gap-1 ml-4">
                                        {pieData.map((item) => (
                                            <div key={item.name} className="flex items-center gap-2">
                                                <div className="w-2 h-2 rounded-full" style={{ background: item.color }} />
                                                <span className="text-xs text-muted-foreground">{item.name}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    <WorkoutHistoryList workoutList={workoutList} stepsHistory={stepsHistory} todaySteps={todaySteps} showHistory={showHistory} setShowHistory={setShowHistory} editingEntry={editingEntry} setEditingEntry={setEditingEntry} handleUpdateWorkout={handleUpdateWorkout} handleDeleteWorkout={handleDeleteWorkout} language={language} />
                </div>
            </motion.div>

            {/* Quick Log Drawer */}
            <AnimatePresence>
                {quickLogOpen && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
                            onClick={() => setQuickLogOpen(false)}
                        />
                        <motion.div
                            initial={{ y: '100%' }}
                            animate={{ y: 0 }}
                            exit={{ y: '100%' }}
                            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                            className="fixed bottom-0 left-0 right-0 z-50 bg-card rounded-t-3xl border-t border-border shadow-2xl max-h-[85vh] overflow-y-auto"
                        >
                            <div className="flex justify-center py-3">
                                <div className="w-12 h-1.5 bg-muted rounded-full" />
                            </div>
                            
                            <div className="px-4 pb-32 space-y-3">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-lg font-bold text-foreground flex items-center gap-2">
                                        <Dumbbell className="w-5 h-5 icon-fitness" />
                                        {language === 'de' ? 'Workout eintragen' : 'Log Workout'}
                                    </h3>
                                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setQuickLogOpen(false)}>
                                        <X className="w-4 h-4" />
                                    </Button>
                                </div>

                                {/* Date */}
                                <div className="text-center py-1.5 px-3 rounded-xl bg-primary/10 text-primary text-sm font-medium">
                                    {selectedDate.toLocaleDateString(language === 'de' ? 'de-DE' : 'en-US', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                                </div>

                                {/* Workout Type Picker */}
                                <div>
                                    <Label className="text-muted-foreground text-sm mb-1.5 block">
                                        {language === 'de' ? 'Sportart' : 'Sport'}
                                    </Label>
                                    {/* Quick-pick favorites (always visible, no dropdown needed) */}
                                    {favoriteWorkouts && favoriteWorkouts.length > 0 && (
                                        <div className="mb-2 -mt-1" data-testid="fitness-favorites-quickpick">
                                            <div className="flex items-center gap-1.5 mb-1.5">
                                                <Star className="w-3 h-3 text-amber-500 fill-amber-500" />
                                                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                                                    {language === 'de' ? 'Favoriten' : 'Favorites'}
                                                </span>
                                            </div>
                                            <div className="flex flex-wrap gap-1.5">
                                                {favoriteWorkouts.map((fav) => {
                                                    const wt = getWorkoutType(fav.workout_type);
                                                    const Icon = wt?.icon;
                                                    const label = wt?.label?.[language] || fav.workout_type;
                                                    const isActive = quickForm.workout_type === fav.workout_type;
                                                    return (
                                                        <button
                                                            type="button"
                                                            key={fav.workout_type}
                                                            onClick={() => setQuickForm({ ...quickForm, workout_type: fav.workout_type })}
                                                            data-testid={`fitness-fav-chip-${fav.workout_type}`}
                                                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
                                                                isActive
                                                                    ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                                                                    : 'bg-amber-50 dark:bg-amber-950/30 text-amber-900 dark:text-amber-100 border-amber-200 dark:border-amber-800/50 hover:bg-amber-100 dark:hover:bg-amber-900/40'
                                                            }`}
                                                            style={!isActive && wt?.color ? { color: wt.color } : undefined}
                                                        >
                                                            {Icon && <Icon className="w-3.5 h-3.5" />}
                                                            <span className="truncate max-w-[120px]">{label}</span>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                    <WorkoutPicker
                                        value={quickForm.workout_type}
                                        onChange={(v) => setQuickForm({ ...quickForm, workout_type: v })}
                                        favorites={favoriteWorkouts}
                                    />
                                </div>

                                {/* Golf: 9/18 Loch */}
                                {quickForm.workout_type === 'golf' && (
                                    <div className="flex gap-2">
                                        {[9, 18].map((holes) => (
                                            <Button
                                                key={holes}
                                                variant={quickForm.golf_holes === holes ? 'default' : 'outline'}
                                                size="sm"
                                                onClick={() => setQuickForm({...quickForm, golf_holes: holes, duration_seconds: holes === 18 ? 240 * 60 : 120 * 60})}
                                                className={`flex-1 ${quickForm.golf_holes === holes ? 'bg-primary' : ''}`}
                                            >
                                                {holes} {language === 'de' ? 'Loch' : 'Holes'}
                                            </Button>
                                        ))}
                                    </div>
                                )}

                                {/* Duration H:M:S */}
                                <div>
                                    <Label className="text-muted-foreground text-sm mb-1.5 block">
                                        {language === 'de' ? 'Dauer' : 'Duration'}
                                    </Label>
                                    <DurationPicker
                                        totalSeconds={quickForm.duration_seconds}
                                        onChange={(sec) => setQuickForm({ ...quickForm, duration_seconds: sec })}
                                    />
                                </div>

                                {/* Location/Comment with Voice */}
                                <div className="flex gap-2 items-center">
                                    <Input
                                        value={quickForm.location}
                                        onChange={(e) => setQuickForm({...quickForm, location: e.target.value})}
                                        placeholder={
                                            quickForm.workout_type === 'golf' ? (language === 'de' ? 'Golfplatz (z.B. Sempachersee)' : 'Golf course') :
                                            quickForm.workout_type === 'cycling' ? (language === 'de' ? 'Route (z.B. Rigi Rundfahrt)' : 'Route') :
                                            quickForm.workout_type === 'hiking' ? (language === 'de' ? 'Wanderung (z.B. Pilatus)' : 'Hike name') :
                                            quickForm.workout_type === 'swimming' ? (language === 'de' ? 'Schwimmbad / See' : 'Pool / Lake') :
                                            (language === 'de' ? 'Ort / Kommentar' : 'Location / Comment')
                                        }
                                        className="bg-muted border-2 border-border h-10 text-sm flex-1"
                                        data-testid="workout-location"
                                    />
                                    <VoiceInput onResult={(text) => setQuickForm({...quickForm, location: text})} />
                                </div>
                                <MemorySuggestions 
                                    category="workout_location"
                                    currentValue={quickForm.location}
                                    onSelect={(s) => setQuickForm({...quickForm, location: s.name})}
                                />

                                {/* Estimated Calories */}
                                <div className="text-center py-1">
                                    <p className="text-xs text-muted-foreground">{language === 'de' ? 'Geschätzte Kalorien' : 'Est. Calories'}</p>
                                    <p className="text-xl font-bold text-primary">
                                        {estimating ? <Loader2 className="w-5 h-5 animate-spin inline" /> : `${quickForm.calories_burned} kcal`}
                                    </p>
                                </div>

                                <div className="flex gap-2">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={handleSaveAsFavorite}
                                        disabled={savingFavorite || quickLoading}
                                        className="h-14 px-4 rounded-xl border-amber-400/40 text-amber-500 hover:bg-amber-400/10"
                                        data-testid="fitness-save-favorite-btn"
                                        title={language === 'de' ? 'Als Favorit speichern' : 'Save as favorite'}
                                    >
                                        {savingFavorite ? <Loader2 className="w-5 h-5 animate-spin" /> : <Star className="w-5 h-5" />}
                                    </Button>
                                    <Button
                                        onClick={handleQuickLog}
                                        disabled={quickLoading || !quickForm.duration_seconds}
                                        className="flex-1 h-14 bg-primary text-primary-foreground hover:bg-primary/90 rounded-xl text-base font-bold"
                                        data-testid="fitness-save-btn"
                                    >
                                        {quickLoading ? (
                                            <Loader2 className="w-5 h-5 animate-spin" />
                                        ) : (
                                            <>
                                                <Check className="w-5 h-5 mr-2" />
                                                {language === 'de' ? 'OK — Speichern' : 'OK — Save'}
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </div>
    );
}
